import React from 'react';
import {Navigate, NavLink} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {paginaAiuto, pagineAiuto, pieDiPagina, testata} from '../../moduli/leggi';
import {useSkin} from '../../moduli/useSkin';
import Nota from './Nota';
import {impagina} from './impagina';
import {Scrivici} from './Backend';
import ColonnaConto from '../Nova/ColonnaConto';
import IconaNova, {iconaDaEmoji} from '../Nova/IconaNova';
import {eOspite} from '../../utils/currentUser';
import './Vetrina.css';

/** Vero dal computer (1024px in su), e si aggiorna se la finestra cambia. */
export function useComputer() {
  const chiedi = () => typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(min-width: 1024px)').matches;
  const [computer, setComputer] = React.useState(chiedi);
  React.useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const m = window.matchMedia('(min-width: 1024px)');
    const su = () => setComputer(m.matches);
    su();
    m.addEventListener('change', su);
    return () => m.removeEventListener('change', su);
  }, []);
  return computer;
}

/* I titoli di serie di tutte le pagine di Aiuto per la colonna di Nova, tradotti finche' la skin non li cambia. */
const ITALIANI = {chiSiamo: 'Chi siamo', regolamento: 'Regolamento', assistenza: 'Assistenza', terminiBonus: 'Termini dei bonus', privacy: 'Privacy', cookie: 'Cookie'};
function titoloDellaColonna(p, t) {
  if (p.titolo !== ITALIANI[p.chiave]) return p.titolo;
  if (p.chiave === 'terminiBonus') return t('Termini dei bonus');
  if (p.chiave === 'privacy') return t('Privacy');
  if (p.chiave === 'cookie') return t('Cookie');
  return titoloDi(p.chiave, t);
}

/**
 * LA COLONNA DI AIUTO (stile Nova, dal computer, disegno Aiuto): a sinistra
 * le pagine di Aiuto accese e, sotto, «In questa pagina» con le sezioni del
 * testo (le stesse dell'indice). Sotto i 1024px non c'e' (Nova.css). La
 * monta anche Chi siamo a sezioni (PaginaChiSiamo), senza indice.
 */
export function ColonnaAiuto({indice = [], vai}) {
  const {t} = useTranslation();
  const pagine = pagineAiuto().filter((p) => p.acceso);
  return (
    <aside className={'vet-aiuto-colonna'}>
      <nav className={'vet-aiuto-pagine'} aria-label={t('Aiuto')}>
        {pagine.map((p) => (
          <NavLink key={p.chiave} to={p.percorso} className={({isActive}) => 'vet-aiuto-pagina' + (isActive ? ' active' : '')}>
            {titoloDellaColonna(p, t)}
          </NavLink>
        ))}
      </nav>
      <Indice indice={indice} vai={vai}/>
    </aside>
  );
}

/** «In questa pagina»: le sezioni del testo, sotto la colonna. */
function Indice({indice, vai}) {
  const {t} = useTranslation();
  if (!indice.length) return null;
  return (
    <nav className={'vet-aiuto-indice'} aria-label={t('In questa pagina')}>
      <p>{t('In questa pagina')}</p>
      {indice.map((s) => <a key={s.id} href={'#' + s.id} onClick={(e) => vai(e, s.id)}>{s.titolo}</a>)}
    </nav>
  );
}

/**
 * I CANALI DELL'ASSISTENZA (stile Nova, dal computer): tre riquadri sopra il
 * testo, con quello che la skin ha gia' scritto nel pie' di pagina: la chat
 * su Telegram (Marchio › Telegram), l'email (Colonna Aiuto › Email) e le
 * domande frequenti (se nel testo ci sono domande). Un canale non scritto
 * non c'e': niente di inventato.
 */
function Canali({domande}) {
  const {t} = useTranslation();
  const piede = pieDiPagina();
  const telegram = ((piede.marchio && piede.marchio.social) || []).find((s) => s.chiave === 'telegram');
  const email = piede.colonnaAiuto && piede.colonnaAiuto.email;
  const canali = [
    telegram && {href: telegram.indirizzo, esterno: true, icona: 'aereo', titolo: t('Chat su Telegram'), testo: t('Il canale più veloce: apri la chat e scrivici.')},
    email && {href: 'mailto:' + email, icona: 'busta', titolo: t('E-mail'), testo: email},
    domande && {href: '#domande', icona: 'domanda', titolo: t('Domande frequenti'), testo: t('Le risposte alle domande che riceviamo di più.')},
  ].filter(Boolean);
  if (!canali.length) return null;
  return (
    <div className={'vet-aiuto-canali'}>
      {canali.map((c) => (
        <a key={c.titolo} href={c.href} className={'vet-aiuto-canale'} target={c.esterno ? '_blank' : undefined} rel={c.esterno ? 'noopener noreferrer' : undefined}>
          <span className={'vet-aiuto-canale-icona'} aria-hidden={'true'}><IconaNova nome={c.icona} misura={24}/></span>
          <strong>{c.titolo}</strong>
          <span>{c.testo}</span>
        </a>
      ))}
    </div>
  );
}

/* I titoli di serie delle pagine, scritti come t('…') con la frase fissa:
   cosi' la raccolta delle parole (scripts/raccogli-parole.mjs) li vede e il
   dizionario li ha. Con t(TITOLI[chiave]) non li vedrebbe nessuno. */
function titoloDi(chiave, t) {
  if (chiave === 'chiSiamo') return t('Chi siamo');
  if (chiave === 'assistenza') return t('Assistenza');
  if (chiave === 'giocoResponsabile') return t('Gioco responsabile');
  return t('Regolamento');
}

/* Dove si scrive il testo di una pagina, per la nota quando manca. */
function doveSiScrive(chiave, t) {
  if (chiave === 'giocoResponsabile') return t('Moduli → Piè di pagina → Informazioni → Gioco responsabile');
  return t('Moduli → Aiuto → {{pagina}}', {pagina: titoloDi(chiave, t)});
}

/**
 * Le pagine di testo: Chi siamo, Regolamento, Assistenza (il gruppo
 * «Aiuto» del menu, modulo Aiuto) e Gioco responsabile (modulo Pie' di
 * pagina). Titolo e testo li scrive la skin dal backoffice; e' testo
 * semplice, gli a capo restano a capo, e poche regole di scrittura (righe
 * in maiuscolo, «•», «1)», un'emoji in testa) diventano titoli, indice,
 * elenchi e riquadri: sono in impagina.js. Finche' la skin non l'ha scritto si
 * vede il posto con scritto da dove arriva, non un testo inventato.
 *
 * Una pagina di Aiuto spenta dalla skin non c'e': l'indirizzo porta alla
 * Home, come una sezione spenta. Regolamento e Assistenza senza testo nel
 * modulo Aiuto mostrano quello scritto prima nel Pie' di pagina.
 *
 * Il testo scritto dalla skin e' in una lingua sola, quella in cui l'ha
 * scritto il backoffice: i testi lunghi per lingua sono un lavoro a parte
 * («Testi lunghi» in Lingue e parole, spento finche' non si decide). Il
 * titolo di serie invece passa dal dizionario: si traduce finche' la skin
 * non ne scrive uno suo.
 */
export default function PaginaTesto({chiave, userService}) {
  useSkin();
  const {t} = useTranslation();
  const computer = useComputer();
  const aiuto = paginaAiuto(chiave);
  const informazioni = pieDiPagina().informazioni;
  const testo = aiuto
    ? aiuto.testo
    : (informazioni && typeof informazioni[chiave] === 'string' ? informazioni[chiave].trim() : '');
  const {blocchi, indice} = React.useMemo(() => impagina(testo), [testo]);
  if (aiuto && !aiuto.acceso) return <Navigate to={'/'} replace/>;
  /* Il titolo scritto dalla skin vale cosi' com'e'; quello di serie si traduce. */
  const diSerie = titoloDi(chiave, t);
  const titolo = aiuto && aiuto.titolo && aiuto.titolo !== diSerieItaliano(chiave) ? aiuto.titolo : diSerie;
  /* CON LO STILE NOVA DAL COMPUTER (disegno Aiuto): la colonna a sinistra
     con le pagine e «In questa pagina», i canali dell'assistenza sopra il
     testo e le domande che si aprono e chiudono. Sul telefono, e nelle
     altre skin, la pagina resta com'e'. */
  const nova = computer && testata().stile === 'nova';
  /* GIOCO RESPONSABILE, PER CHI E' DENTRO, STA NEL CONTO: la colonna e'
     quella del conto (con la voce accesa), non quella di Aiuto, da
     qualunque parte ci si arrivi (menu Account, colonna del conto, piede).
     Chi e' ospite il conto non ce l'ha: per lui resta in Aiuto. */
  const nelConto = nova && chiave === 'giocoResponsabile' && !eOspite();
  const vai = (e, id) => {
    e.preventDefault();
    const dove = document.getElementById(id);
    if (dove) dove.scrollIntoView({behavior: 'smooth', block: 'start'});
  };
  return (
    <div className={'vetrina vet-pagina-testo' + (nova ? ' vet-aiuto-nova' : '')}>
      {nova && (nelConto
        ? <ColonnaConto userService={userService}><Indice indice={indice} vai={vai}/></ColonnaConto>
        : <ColonnaAiuto indice={indice} vai={vai}/>)}
      <section className={'vet-blocco'} aria-label={titolo}>
        <div className={'vet-testata'}><h2>{titolo}</h2></div>
        {nova && chiave === 'assistenza' && <Canali domande={blocchi.some((b) => b.tipo === 'domanda')}/>}
        {testo
          ? <TestoImpaginato testo={testo} domandeChiuse={nova}/>
          : (
            <Nota tono={'spento'} giocatore={t('Questa pagina non è ancora disponibile.')}>
              {t('Questo testo lo scrive la skin dal backoffice: {{dove}}. Oggi non è ancora stato scritto.', {dove: doveSiScrive(chiave, t)})}
            </Nota>
          )}
        {aiuto && aiuto.scrivici && <Scrivici/>}
      </section>
    </div>
  );
}

/**
 * Il testo della skin con titoli, indice, elenchi e riquadri (regole in
 * impagina.js). Con `domandeChiuse` una domanda e il paragrafo che la segue
 * diventano una voce che si apre e si chiude (le domande frequenti di
 * Nova): la prima e' aperta, le altre chiuse.
 */
export function TestoImpaginato({testo, domandeChiuse}) {
  const {t} = useTranslation();
  const {blocchi, indice} = React.useMemo(() => impagina(testo), [testo]);
  /* L'indice scorre alla sezione senza toccare l'indirizzo: il router non se ne accorge. */
  const vai = (e, id) => {
    e.preventDefault();
    const dove = document.getElementById(id);
    if (dove) dove.scrollIntoView({behavior: 'smooth', block: 'start'});
  };
  let domande = 0;
  const disegnati = blocchi.map((b, i) => {
    if (domandeChiuse && b.tipo === 'domanda' && blocchi[i + 1] && blocchi[i + 1].tipo === 'paragrafo') {
      domande += 1;
      return (
        <details key={i} className={'vet-imp-faq'} open={domande === 1} id={domande === 1 ? 'domande' : undefined}>
          <summary>{b.testo}</summary>
          <p className={'vet-testo-lungo'}>{blocchi[i + 1].testo}</p>
        </details>
      );
    }
    if (domandeChiuse && b.tipo === 'paragrafo' && i > 0 && blocchi[i - 1].tipo === 'domanda') return null;
    if (b.tipo === 'sezione') return <h3 key={i} id={b.id} className={'vet-imp-sezione'}><span>{b.numero}</span>{b.titolo}</h3>;
    if (b.tipo === 'sottotitolo') return <h4 key={i} className={'vet-imp-sottotitolo'}>{b.testo}</h4>;
    if (b.tipo === 'domanda') return <p key={i} className={'vet-imp-domanda'}>{b.testo}</p>;
    if (b.tipo === 'punti') return <ul key={i} className={'vet-imp-elenco'}>{b.voci.map((v, j) => <li key={j}>{v}</li>)}</ul>;
    if (b.tipo === 'numeri') return <ol key={i} className={'vet-imp-elenco'}>{b.voci.map((v, j) => <li key={j}>{v}</li>)}</ol>;
    if (b.tipo === 'riquadro') {
      return (
        <div key={i} className={'vet-imp-riquadro'}>
          <p className={'vet-imp-riquadro-titolo'}><span aria-hidden={'true'}><IconaNova nome={iconaDaEmoji(b.emoji)} misura={20}/></span>{b.titolo}</p>
          {b.righe.length > 0 && <p className={'vet-testo-lungo'}>{b.righe.join('\n')}</p>}
        </div>
      );
    }
    return <p key={i} className={'vet-testo-lungo'}>{b.testo}</p>;
  });
  /* L'indice va prima della prima sezione: sopra resta l'introduzione. */
  const prima = blocchi.findIndex((b) => b.tipo === 'sezione');
  if (indice.length) {
    disegnati.splice(prima, 0, (
      <nav key={'indice'} className={'vet-imp-indice'} aria-label={t('Indice')}>
        <p className={'vet-imp-indice-titolo'}>{t('Indice')}</p>
        <ol>
          {indice.map((s) => <li key={s.id} value={Number(s.numero)}><a href={'#' + s.id} onClick={(e) => vai(e, s.id)}>{s.titolo}</a></li>)}
        </ol>
      </nav>
    ));
  }
  return <div className={'vet-impaginato'}>{disegnati}</div>;
}

/** Il titolo di serie in italiano (quello del catalogo): se la skin non l'ha cambiato, si mostra tradotto. */
function diSerieItaliano(chiave) {
  return {chiSiamo: 'Chi siamo', regolamento: 'Regolamento', assistenza: 'Assistenza'}[chiave] || '';
}
